import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import useEstMobile from '../hooks/useEstMobile';
import {
  IconeDossier, IconeHorloge, IconeValide, IconeRefuse, IconeCrayon, IconePlume,
  IconeEnvoi, IconeCouronne, IconeRafraichir, IconeListe, IconeNote, IconeAvion,
  IconeRecu, IconeDocument, IconeFeu, IconeTrombone,
} from '../components/IconesSVG';

import MenuProfil from '../components/MenuProfil';
const STATUTS = {
  brouillon:               { label: 'Brouillon',         color: '#888',    bg: '#F2F3F4', icon: IconeCrayon },
  en_controle_terrain:     { label: 'En cours',          color: '#9A7D0A', bg: '#FEF9E7', icon: IconeHorloge },
  en_memo_filiale:         { label: 'Mémo Filiale',      color: '#9A7D0A', bg: '#FEF9E7', icon: IconeHorloge },
  en_signature_dg_filiale: { label: 'Chez DG Filiale',   color: '#1A5E8A', bg: '#EAF4FB', icon: IconePlume },
  envoye_holding:          { label: 'Chez Holding',      color: '#1A5E8A', bg: '#EAF4FB', icon: IconeEnvoi },
  dans_parapheur_pdg:      { label: 'Chez PDG',          color: '#6C3483', bg: '#F5EEF8', icon: IconeCouronne },
  solde:                   { label: 'Approuvé',          color: '#1E8449', bg: '#EAFAF1', icon: IconeValide },
  en_correction:           { label: 'À corriger',        color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_memo:      { label: 'Mémo à corriger',   color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_holding:   { label: 'Holding à corriger',color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_pdg:       { label: 'PDG à corriger',    color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  rejete:                  { label: 'Rejeté',            color: '#96281B', bg: '#FDEDEC', icon: IconeRefuse },
};

const TYPES = {
  expression_besoin: { label: 'Fiche EB',       icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon commande',    icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Frais mission',   icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture',         icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Régularisation',  icon: IconeRafraichir, color: '#935116' },
};

export default function DashboardDemandeur() {
  const estMobile = useEstMobile();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [pochettes, setPochettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [showListe, setShowListe] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- effet volontairement limite au montage
  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      setLoading(true);
      const res = await api.get('/demandes');
      const data = res.data?.data || res.data || [];
      const miennes = data.filter(d =>
        d.demandeur_id === (user?.id || user?._id) ||
        d.demandeur?.id === (user?.id || user?._id) ||
        d.demandeur?._id === (user?.id || user?._id)
      );
      setPochettes(miennes);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' FCFA' : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—';

  const pochettesFiltrees = filtreStatut === 'tous'
    ? pochettes
    : filtreStatut === 'en_cours'
    ? pochettes.filter(p => !['solde', 'rejete', 'brouillon'].includes(p.statut))
    : pochettes.filter(p => p.statut === filtreStatut);

  const stats = {
    total: pochettes.length,
    en_cours: pochettes.filter(p => !['solde', 'rejete', 'brouillon'].includes(p.statut)).length,
    solde: pochettes.filter(p => p.statut === 'solde').length,
    rejete: pochettes.filter(p => p.statut === 'rejete').length,
  };

  const heure = new Date().getHours();
  const salutation = heure < 12 ? 'Bonjour' : heure < 18 ? 'Bon après-midi' : 'Bonsoir';

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div data-entete style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.png" alt="JETA" style={{ height: 32 }} onError={e => { e.target.style.display = 'none'; }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>JETA GED</div>
            <div style={{ fontSize: 11, color: '#8899AA' }}>{user?.roles?.[0]?.filiale?.nom || 'JETA Groupe'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MenuProfil />
        </div>
      </div>

      <div style={s.body}>
        {/* BIENVENUE */}
          <div style={estMobile ? { ...s.welcomeCard, flexDirection: 'column', alignItems: 'stretch', gap: 14 } : s.welcomeCard}>
          <div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>{salutation}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{user?.prenom} {user?.nom}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <button onClick={() => navigate('/nouveau-document')}
            style={estMobile ? { ...s.btnNouveauGrand, width: '100%', justifyContent: 'center' } : s.btnNouveauGrand}>
            + Nouvelle demande
          </button>
        </div>

        {/* STATS RAPIDES */}
        <div style={estMobile ? { ...s.statsRow, gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 } : s.statsRow}>
          {[
            { label: 'Total',    value: stats.total,    color: '#0D1B2A', bg: '#F5F6FA', Icone: IconeDossier, filtre: 'tous' },
            { label: 'En cours', value: stats.en_cours, color: '#9A7D0A', bg: '#FEF9E7', Icone: IconeHorloge, filtre: 'en_cours' },
            { label: 'Approuvés',value: stats.solde,    color: '#1E8449', bg: '#EAFAF1', Icone: IconeValide,  filtre: 'solde' },
            { label: 'Rejetés',  value: stats.rejete,   color: '#96281B', bg: '#FDEDEC', Icone: IconeRefuse,  filtre: 'rejete' },
          ].map((st, i) => (
            <div key={i} onClick={() => { setFiltreStatut(st.filtre); setShowListe(true); }}
              style={{ ...s.statCard, border: filtreStatut === st.filtre && showListe ? `2px solid ${st.color}` : '2px solid transparent', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{st.label}</div>
                  <div style={{ fontSize: estMobile ? 22 : 28, fontWeight: 800, color: st.color }}>{st.value}</div>
                </div>
                <div style={{ width: estMobile ? 34 : 44, height: estMobile ? 34 : 44, borderRadius: 12, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: st.color, flexShrink: 0 }}>
                  <st.Icone taille={estMobile ? 18 : 22} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* BOUTON TOGGLE LISTE */}
        <div style={{ marginBottom: 14 }}>
          <button
            type="button"
            onClick={() => setShowListe(prev => !prev)}
            style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: showListe ? '#0D1B2A' : 'linear-gradient(135deg, #2980B9, #1A5276)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(41,128,185,0.3)' }}>
            {showListe ? <><IconeListe taille={15} /> Masquer mes demandes</> : <><IconeListe taille={15} /> Voir mes demandes</>}
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '2px 10px', fontSize: 12 }}>
              {pochettesFiltrees.length}
            </span>
          </button>
        </div>

        {/* LISTE DOCUMENTS — affichee uniquement si showListe */}
        {showListe && (
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A' }}>
                {filtreStatut === 'tous' ? 'Tous mes documents' :
                 filtreStatut === 'en_cours' ? 'Documents en cours' :
                 filtreStatut === 'solde' ? 'Documents approuvés' : 'Documents rejetés'}
                <span style={{ marginLeft: 8, fontSize: 12, color: '#888', fontWeight: 400 }}>
                  ({pochettesFiltrees.length})
                </span>
              </div>
              {filtreStatut !== 'tous' && (
                <button onClick={() => setFiltreStatut('tous')}
                  style={{ fontSize: 12, color: '#2980B9', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Voir tout
                </button>
              )}
            </div>

            {loading ? (
              <div style={s.emptyState}>
                <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>
                Chargement...
              </div>
            ) : pochettesFiltrees.length === 0 ? (
              <div style={s.emptyState}>
                <div style={{ marginBottom: 14, color: '#ccc' }}><IconeDossier taille={56} /></div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 6 }}>
                  Aucun document
                </div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                  Créez votre première demande
                </div>
                <button onClick={() => navigate('/nouveau-document')} style={s.btnNouveau}>
                  + Nouvelle demande
                </button>
              </div>
            ) : (
              <div style={s.docsList}>
                {pochettesFiltrees.map(p => {
                  const statut = STATUTS[p.statut] || STATUTS.brouillon;
                  const type = TYPES[p.type_document] || { icon: IconeDocument, color: '#666', label: p.type_document };
                  return (
                    <div key={p.id} onClick={() => navigate('/demandes/' + p.id)} style={s.docCard}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: type.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: type.color, flexShrink: 0 }}>
                        <type.icon taille={24} />
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A' }}>{p.reference}</span>
                          {p.priorite === 'P1' && (
                            <span style={{ fontSize: 10, background: '#FDEDEC', color: '#C0392B', padding: '2px 8px', borderRadius: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}><IconeFeu taille={11} /> Urgent</span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: '#555', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.titre || p.motif || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: '#aaa', display: 'flex', gap: 8 }}>
                          <span>{type.label}</span>
                          <span>·</span>
                          <span>{formatDate(p.created_at)}</span>
                          {(p.documents?.length || 0) > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>· <IconeTrombone taille={12} /> {p.documents.length}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: '#C0392B' }}>
                          {formatMontant(p.montant)}
                        </span>
                        <span style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, background: statut.bg, color: statut.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <statut.icon taille={12} /> {statut.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F5F6FA', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { background: '#0D1B2A', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: '#C0392B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' },
  btnLogout: { background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 18, padding: 4 },
  body: { maxWidth: 800, margin: '0 auto', padding: '16px 20px' },
  welcomeCard: { background: 'linear-gradient(135deg, #0D1B2A, #1A3A5C)', borderRadius: 14, padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 },
  statCard: { background: '#fff', borderRadius: 12, padding: '16px', border: '2px solid transparent', transition: '0.15s' },
  section: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', overflow: 'hidden' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB' },
  docsList: { display: 'flex', flexDirection: 'column' },
  docCard: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', transition: '0.1s' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#888', textAlign: 'center' },
  btnNouveau: { padding: '16px 32px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #C0392B, #962D22)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 18px rgba(192,57,43,0.35)', transition: 'transform 0.15s, box-shadow 0.15s' },
  btnNouveauGrand: { padding: '14px 28px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #C0392B, #962D22)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 20px rgba(192,57,43,0.4)', transition: 'transform 0.15s, box-shadow 0.15s', flexShrink: 0 },
};
